import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { authService } from '../../data';
import { log } from '../../utils/logger';
import { useAuth } from '../../App';
import { getApi } from '../../utils/api';
import {
  resolveUserStores,
  navigateForStores,
  withStores,
} from '../../utils/storeRouting';

const AUTH_TOKEN_KEY = process.env.REACT_APP_AUTH_TOKEN_KEY;

const Verify = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setIsAuthed, setUser, setStores } = useAuth();
  const [verificationStatus, setVerificationStatus] = React.useState('verifying');
  const [error, setError] = React.useState('');

  const persistToken = React.useCallback((result) => {
    if (result.token && result.token.split('.').length === 3) {
      localStorage.setItem(AUTH_TOKEN_KEY, result.token);
      getApi().instance.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
      return;
    }
    if (result.user?.token && result.user.token.split('.').length === 3) {
      localStorage.setItem(AUTH_TOKEN_KEY, result.user.token);
      getApi().instance.defaults.headers.common['Authorization'] = `Bearer ${result.user.token}`;
    }
  }, []);

  const completeVerification = React.useCallback(async () => {
    try {
      log('Verifying token:', { original: token, encoded: encodeURIComponent(token) });
      getApi().instance.defaults.headers['X-React-Component'] = 'Verify';

      const result = await authService.verifyMagicLink(token);
      log('Verification result:', result);

      if (!result.success) {
        setError(result.message || 'Verification failed');
        setVerificationStatus('error');
        return;
      }

      persistToken(result);
      setIsAuthed(true);

      let resolvedStores;
      try {
        resolvedStores = await resolveUserStores(result.user);
      } catch (storeError) {
        log('Failed to resolve stores after verify:', storeError);
        // Do not treat fetch failure as "no stores" — send to / so AuthProvider can retry
        setUser(withStores(result.user, result.user?.stores || []));
        setStores(Array.isArray(result.user?.stores) ? result.user.stores : []);
        navigate('/');
        return;
      }

      const hydratedUser = withStores(result.user, resolvedStores);
      setUser(hydratedUser);
      setStores(resolvedStores);
      navigateForStores(navigate, resolvedStores);
    } catch (err) {
      log('Error during verification:', err);
      setError(err.message || 'An error occurred during verification');
      setVerificationStatus('error');
    }
  }, [token, navigate, setIsAuthed, setUser, setStores, persistToken]);

  React.useEffect(() => {
    if (token) {
      completeVerification();
    } else {
      setError('No verification token provided');
      setVerificationStatus('error');
    }
  }, [token, completeVerification]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
      p={3}
    >
      {verificationStatus === 'verifying' && (
        <>
          <CircularProgress />
          <Typography>Verifying your login...</Typography>
        </>
      )}

      {verificationStatus === 'error' && (
        <>
          <Typography variant="h6" color="error">Verification Failed</Typography>
          <Typography color="error">{error}</Typography>
          <Typography
            onClick={() => navigate('/login')}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
              mt: 2
            }}
          >
            Return to Login
          </Typography>
        </>
      )}
    </Box>
  );
};

export default Verify;
